import { FacebookOutlined, InstagramOutlined } from '@ant-design/icons';
import { UserOutlined } from '@ant-design/icons';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ShareIcon,
} from '@heroicons/react/outline';
import {
  Affix,
  Avatar,
  Badge,
  Button,
  Dropdown,
  MenuProps,
  Modal,
  Progress,
  Typography,
} from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import moment from 'moment';
import { IBM_Plex_Sans } from 'next/font/google';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import Renderer from '../../Editor/Reader';
import SchoolFeaturedCard from '../../SchoolsPage/SchoolCard';
import { AppleLogo, GoogleIcon } from '../../../../../assets';
import BackIconChevron from '../../../../../assets/Icons/BackIconChevron';
import CopyClipBoard from '../../../../../assets/Icons/CopyClipBoard';
import LinkedInIcon from '../../../../../assets/Icons/LinkedInIcon';
import WhatsAppIcon from '../../../../../assets/Icons/WhatsAppIcon';
import {
  authModalControl,
  tokenUpdate,
} from '../../../../atoms/authModalControl';
import { authModalDrawerController } from '../../../../atoms/authModalDrawerController';
import { user } from '../../../../atoms/user';
import appwriteService from '../../../../config/appwrite';
import {
  EventDetails,
  logEvent,
} from '../../../../hooks/useClickEventTracking';
import { getBlog } from '../../../../services/blog';
import {
  IUpdateReadingHistory,
  updateFollow,
  updateLike,
  updateReadingHistory,
} from '../../../../services/School';
import { letterColors } from '../../../../utils/AvatarColor';
import { SchoolsDashBoardData } from '../../../../utils/interfaces/Schools';
import { User } from '../../../../utils/interfaces/User';
import { getUserInitials } from '../../../ChatBox/Message';
import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import ParserV2 from '../../../shared/basic/Parser/ParserV2';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { getLoginToken } from '../../../shared/getToken';
import { notify } from '../../../shared/notify';
import HomepageSlider from '../../../V2/HomePage/HomepageSlider';
import LoginDrawer from '../../../V2/LoginDrawer';
import SignUpDrawer from '../../../V2/SignUpDrawer';

const ibmPlexSans = IBM_Plex_Sans({
  subsets: ['latin'],
  weight: ['400', '500', '700'], // Adjust weights as per your need
});

interface likeComponentProps {
  likeCount: number;
  isActive: boolean;
  toggleHeart?: () => void;
}

export const formatCount = (count: number): { formatted: string } => {
  let formatted: string = count.toString();

  if (count >= 1000) {
    formatted = (count / 1000).toFixed(1);
    formatted = formatted.endsWith('.0')
      ? formatted.slice(0, -2) + 'K'
      : formatted + 'K';
  }

  return { formatted };
};

interface Chapter {
  data?: {
    title: string;
    school_id: number;
    slug: string;
    content: string;
    postId: number;
    schoolName: string;
    liked: boolean;
    likeCount: number;
    next_post: {
      slug: string | null;
      title: string;
      content: string;
      postId?: string;
    } | null;
    previous_post: {
      slug: string | null;
      title: string;
      postId?: string;
    } | null;
  };
  schoolData: {
    imgPath: string;
    imgUrl: string;
    categoriesId: number;
    authorData?: {
      name: string;
      readersCount: number;
      image: string;
      isFollowing: boolean;
      authorId: string;
      authorUserName?: string;
      authorAvatar?: string;
    };
    imageUrlBg: string;
    not_monetized?: boolean;
  };
}

const LikeComponent: React.FC<likeComponentProps> = ({
  likeCount,
  isActive,
  toggleHeart,
}) => {
  const formatCountAndPadding = (
    count: number,
  ): { formatted: string; paddingClass: string } => {
    let formatted: string = count.toString();
    let paddingClass = 'pr-3';

    if (count >= 1000) {
      formatted = (count / 1000).toFixed(1);
      formatted = formatted.endsWith('.0')
        ? formatted.slice(0, -2) + 'K'
        : formatted + 'K';
    }

    const length = formatted.length;
    if (length === 3) paddingClass = 'pr-2.5';
    else if (length > 3) paddingClass = 'pr-1';

    return { formatted, paddingClass };
  };
  return (
    <div
      onClick={toggleHeart ?? undefined}
      className={clsx(
        likeCount ? 'w-[65px]' : 'w-8',
        'relative bg-[#222326]  bg-opacity-70 h-8 z-10 rounded-full cursor-pointer no-select transition-all duration-300 ease-in-out',
      )}
    >
      <div
        className={clsx(
          likeCount ? '-right-7 md:-right-8' : '-right-[59px] md:-right-[58px]',
          'absolute -top-[9px] md:-top-2 heart-animation-wrapper transition-all duration-300 ease-in-out',
        )}
      >
        <div className='stage'>
          <div className={`heart ${isActive ? 'is-active' : ''}`}></div>
        </div>
      </div>
      <ShouldRender check={likeCount}>
        <div
          className={clsx(
            'w-full customBadge flex justify-end transition-all duration-300 ease-in-out items-center pt-[4.5px]',
            formatCountAndPadding(likeCount).paddingClass,
          )}
        >
          <Badge
            count={formatCountAndPadding(likeCount).formatted}
            className='customBadge text-white'
            overflowCount={999999}
            style={{ backgroundColor: 'transparent', color: 'white' }}
          />
        </div>
      </ShouldRender>
    </div>
  );
};

const ChaptersPage: React.FC<Chapter> = ({ data, schoolData }) => {
  const [schoolCardData, setSchoolData] = useState<SchoolsDashBoardData[]>([]);
  const [visibleDrawer, setVisibleDrawer] = useRecoilState(
    authModalDrawerController,
  );
  const [height, setHeight] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  const [, setVisible] = useRecoilState(authModalControl);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [likeCount, setLikeCount] = useState(0);
  const [followCount, setFollowCount] = useState(0);

  const [isCopied, setIsCopied] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const userData: User | undefined = useRecoilValue(user);
  const showAppleLogin =
    process.env.NEXT_PUBLIC_SHOW_APPLE_LOGIN === 'showLogin';
  const startTimeRef = useRef<Date | null>(null);
  const { schoolId, school_id, post_id } = router.query;
  const screens = useBreakpoint();

  const avatarSrc =
    schoolData?.authorData?.authorAvatar &&
    `${process.env.NEXT_PUBLIC_STUDIO_PATH}/${schoolData?.authorData?.authorAvatar}`;

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  const fetchDataToken = async () => {
    try {
      const tokens = await getLoginToken();
      if (tokens) {
        handleUpdateReadingHistory({
          ecom_user_id: userData?.id as number,
          post_id: data?.postId as number,
          school_id: data?.school_id as number,
        });
      }
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchDataToken();
  }, [userData]);

  const triggerViewEvent = () => {
    const eventParams: EventDetails = {
      event_name: 'learn_view',
      source: 'schools',
      source_type: 'schools',
      source_id: school_id as string,
      sub_source: 'chapter',
      sub_source_id: post_id as string,
      unit: 'boolean',
      value: 1,
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
    startTimeRef.current = new Date();
  };

  const triggerSessionEvent = (start: Date, end: Date) => {
    const sessionDuration = (end.getTime() - start.getTime()) / 1000;

    const eventParams: EventDetails = {
      event_name: 'learn_session',
      source: 'schools',
      source_type: 'schools',
      source_id: school_id as string,
      sub_source: 'chapter',
      sub_source_id: post_id as string,
      unit: 'seconds',
      value: sessionDuration,
      activity_start: moment(start).format('YYYY-MM-DD HH:mm:ss'),
      activity_end: moment(end).format('YYYY-MM-DD HH:mm:ss'),
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  useEffect(() => {
    triggerViewEvent();

    return () => {
      const currentTime = new Date();
      if (startTimeRef.current) {
        triggerSessionEvent(startTimeRef.current, currentTime);
      }
    };
  }, []);

  useEffect(() => {
    setLikeCount(data?.likeCount || 0);
    setIsActive(data?.liked || false);
    setIsFollowing(schoolData?.authorData?.isFollowing || false);
    setFollowCount(schoolData?.authorData?.readersCount || 0);
  }, [schoolData]);

  const dataArray = useMemo(() => {
    let parsedData;
    if (data?.content && schoolData) {
      parsedData = JSON.parse(data?.content as string);
      const thirtyPercent = Math.floor(parsedData.length * 0.3);

      if (!schoolData.not_monetized) {
        parsedData.splice(thirtyPercent, 0, {
          id: 1,
          type: 'anchor',
          value: schoolData.imgPath,
          url: schoolData.imgUrl,
        });
      }
    }
    return parsedData || [];
  }, [
    data?.content,
    schoolData.imgPath,
    schoolData.imgUrl,
    schoolData.not_monetized,
  ]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' && data?.previous_post) {
      router.push(`/schools/${data.previous_post}/`);
    } else if (event.key === 'ArrowRight' && data?.next_post) {
      router.push(`/schools/${data.next_post.slug}/`);
    }
  };

  const calculateReadingTime = (content: string) => {
    if (!content) {
      return { hours: 0, minutes: 0 };
    }

    const textContent = content.replace(/<[^>]+>/g, '');

    const wordCount = textContent?.split(/\s+/).filter(Boolean).length;
    const wordsPerMinute = 75;
    const readingTimeInMinutes = Math.ceil(wordCount / wordsPerMinute);
    const hours = Math.floor(readingTimeInMinutes / 60);
    const minutes = readingTimeInMinutes % 60;

    return { hours, minutes };
  };

  const onCloseLogin = () => {
    setVisibleDrawer((prevState) => ({
      ...prevState,
      login: false,
    }));
    setScrolledPastThreshold(true);
  };

  const onCloseSignUp = () => {
    setVisibleDrawer((prevState) => ({
      ...prevState,
      signUp: false,
    }));
    setScrolledPastThreshold(true);
  };

  useEffect(() => {
    const storedData: string[] = JSON.parse(
      localStorage.getItem('selectedChapterIds') || '[]',
    );

    const newSchoolId = data?.slug;

    if (newSchoolId && !storedData.includes(newSchoolId)) {
      storedData.push(newSchoolId);

      localStorage.setItem('selectedChapterIds', JSON.stringify(storedData));
    }
  }, [data?.slug]);

  const handleUpdateReadingHistory = async (data: IUpdateReadingHistory) => {
    try {
      await updateReadingHistory({
        school_id: data.school_id,
        post_id: data.post_id,
        ecom_user_id: data?.ecom_user_id as number,
      });
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleGetAllSchools = async () => {
    try {
      const response = await getBlog({
        categoryId: Number(schoolData.categoriesId),
      });
      const { data } = response;
      const formattedData = data?.map((school: any) => ({
        id: school.id,
        desc: school.name,
        chapterNum: school.posts_count,
        image: school.documents.file_path,
        slug: school?.slug,
        // categoryId: school
      }));
      setSchoolData(formattedData);
    } catch (error: any) {
      notify(error, 'error');
    }
  };

  useEffect(() => {
    if (schoolData.categoriesId) {
      handleGetAllSchools();
    }
  }, [schoolData.categoriesId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const calculateHeight = () => {
      const windowHeight = window.innerHeight;
      const fortyPercentHeight = windowHeight * (screens.md ? 0.55 : 0.6);
      setHeight(Math.ceil(fortyPercentHeight));
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      const scrollPercentage =
        (scrollTop / (scrollHeight - clientHeight)) * 100;

      if (scrollPercentage >= 15 && !scrolledPastThreshold) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }

      // Check if the user has scrolled back up
      if (scrollPercentage > 15) {
        setScrolledPastThreshold(false);
      }
    };

    // Add event listener for scrolling
    window.addEventListener('scroll', handleScroll);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolledPastThreshold]);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      await appwriteService.loginWithGoogle();
    } catch (error) {
      console.error('Error logging in with Google:', error);
    } finally {
      setGoogleLoading(false);
      setVisible((prevState) => ({
        ...prevState,
        login: false,
        signUp: false,
      }));
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAppleLogin = async () => {
    setAppleLoading(true);
    try {
      await appwriteService.loginWithApple();
    } catch (error) {
      console.error('Error logging in with Google:', error);
    } finally {
      setAppleLoading(false);
      setVisible((prevState) => ({
        ...prevState,
        login: false,
        signUp: false,
      }));
    }
  };

  const handleLikeClick = ({ state }: { state: string }) => {
    const eventParams: EventDetails = {
      event_name: state,
      source: 'schools',
      source_type: 'schools',
      source_id: data?.school_id?.toString() as string,
      source_name: data?.schoolName,
      sub_source: 'chapter',
      sub_source_id: post_id as string,
      sub_source_name: data?.title,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'school_reading_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const toggleHeart = () => {
    const newState = !isActive;
    handleLikeClick({
      state: newState ? 'like' : 'unlike',
    });
    // Update states
    setIsActive(newState);
    setLikeCount((count) => (newState ? count + 1 : count - 1));

    // Handle like logic outside of state updates
    handleLike({
      likeable_id: post_id as string,
      likeable_type: 'post',
      liked: newState,
    });
  };

  const shareOnPlatform = (platform: string, e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = encodeURIComponent(
      `${'https://zebralearn.com/dl/'}${router.asPath}`,
    ); // Replace with your actual URL
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    let shareUrl = '';

    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}`;
        break;
      case 'whatsapp':
        shareUrl = isMobile
          ? `whatsapp://send?text=${url}`
          : `https://web.whatsapp.com/send?text=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'instagram':
        // Instagram sharing requires special handling via mobile apps, or manual redirection.
        alert('Instagram sharing is only available via the mobile app.');
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank');
  };

  const handleFollowClick = ({
    state,
    author_id,
    author_name,
  }: {
    state: string;
    author_id: string;
    author_name: string;
  }) => {
    const eventParams: EventDetails = {
      event_name: state,
      source: 'global',
      source_type: 'author',
      source_id: author_id as string,
      source_name: author_name,
      sub_source: 'school',
      sub_source_id: data?.school_id?.toString() as string,
      sub_source_name: data?.title,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'school_reading_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const toggleFollow = () => {
    const newState = !isFollowing;
    handleFollowClick({
      state: newState ? 'follow' : 'unfollow',
      author_id: schoolData?.authorData?.authorId as string,
      author_name: schoolData?.authorData?.authorUserName as string,
    });
    setIsFollowing(newState);
    setFollowCount((count) => (newState ? count + 1 : count - 1));

    handleFollow({
      author_id: schoolData.authorData?.authorId as string,
      is_follower: newState,
    });
  };

  const handleScroll = () => {
    // Ensure window and document are available (only on client-side)
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Use scrollY instead of pageYOffset as it's the recommended property
      const scrollTop = window.scrollY;

      // Get the container element
      const container = document.getElementById('scroll-container');
      if (container) {
        const containerHeight = container.scrollHeight - window.innerHeight;

        // Ensure container height is greater than zero to avoid division by zero
        if (containerHeight > 0) {
          const scrollPercentage = (scrollTop / containerHeight) * 100;
          setScrollProgress(Math.round(scrollPercentage));
        }
      }
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleCopyLink = () => {
    const linkToCopy = `${'https://zebralearn.com/'}${router.asPath}`;

    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      })
      .catch((error) => {
        console.error('Failed to copy text:', error);
      });
  };

  const handleLike = async ({
    likeable_type,
    likeable_id,
    liked,
  }: {
    likeable_type: 'post' | 'school';
    likeable_id: string;
    liked: boolean;
  }) => {
    try {
      await updateLike({
        likeable_type,
        likeable_id,
        liked,
      });
    } catch (error) {
      console.error('Error logging in with Google:', error);
    }
  };

  const handleFollow = async ({
    author_id,
    is_follower,
  }: {
    author_id: string;
    is_follower: boolean;
  }) => {
    try {
      await updateFollow({
        author_id,
        is_follower,
      });
    } catch (error) {
      console.error('Error :', error);
    }
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div
          className='flex gap-x-3 items-center cursor-pointer p-1 '
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyLink();
          }}
        >
          <motion.span
            initial={{ scale: 1 }}
            animate={{ scale: isCopied ? 1.2 : 1 }}
          >
            <CopyClipBoard />
          </motion.span>

          <motion.span initial={{ scale: 1 }} className='text-white'>
            {isCopied ? (
              'Copied!'
            ) : (
              <span className='text-white z-20'>Copy Link</span>
            )}
          </motion.span>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: '2',
      label: (
        <div
          onClick={(e) => shareOnPlatform('facebook', e)}
          className='flex gap-x-3 items-center p-1'
        >
          <span>
            <FacebookOutlined className='text-xl translate-y-[2px]' />{' '}
          </span>
          Facebook
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <div
          onClick={(e) => shareOnPlatform('instagram', e)}
          className='flex gap-x-3 items-center p-1'
        >
          <span>
            <InstagramOutlined className='text-xl translate-y-[2px]' />{' '}
          </span>
          Instagram
        </div>
      ),
    },
  ];

  return (
    <div className={ibmPlexSans.className}>
      <div className='bg-[#121212] w-full' id='scroll-container'>
        <div className='flex flex-col w-full'>
          <div className=' flex lg:justify-center w-full '>
            <div className='flex justify-between w-full relative  items-center text-white pb-8 md:pb-10  pt-5 md:pt-10 xl:w-7/12 lg:w-10/12 md:px-[65px]   xl:px-7 px-[25px]'>
              {/* Background Image */}
              <ShouldRender check={schoolData.imageUrlBg}>
                <div className='absolute inset-0 w-full h-full'>
                  <Image
                    src={schoolData.imageUrlBg as string}
                    alt='Background Image'
                    width={1200}
                    height={600}
                    priority
                    quality={80}
                    className='object-cover w-full h-full pb-1 px-5 back z-0'
                  />
                </div>
              </ShouldRender>

              {/* Blur Layer */}
              <div
                style={{
                  background:
                    'linear-gradient(90deg, #121212 0%,rgba(18, 18, 18, 0) 50.25%,#121212 100%)',
                }}
                className='absolute inset-0  backdrop-blur-md z-10'
              ></div>

              {/* Gradient Layer */}
              <div className='absolute inset-0 customGradientSchool   z-20'></div>

              {/* Content */}
              <div className='text-white flex items-center gap-x-4 z-30 w-full'>
                <div className='flex flex-col w-full'>
                  <div className='flex  justify-between items-center'>
                    <ShouldRender check={screens.md}>
                      <Link href={`/schools/${schoolId}/`}>
                        <span className='text-lg md:text-xl flex md:items-center gap-x-2.5  cursor-pointer hover:text-gray-100 text-[#787878] font-medium'>
                          <span
                            style={{
                              background: '#333332CC',
                            }}
                            className='flex w-6 h-6  rounded-full items-center justify-center'
                          >
                            <BackIconChevron
                              className=' text-white'
                              style={{
                                strokeWidth: '1px',
                              }}
                            />
                          </span>
                          <ParserV2
                            className='text-base text-[#A7A7A7] hover:text-white '
                            maxWords={screens.md ? 7 : 4}
                            text={data?.schoolName as string}
                          />
                        </span>
                      </Link>
                    </ShouldRender>
                    <ShouldRender check={!screens.md}>
                      <div className='flex flex-col gap-y-5 w-full'>
                        <div className='flex justify-between w-full items-center'>
                          <Link href={`/schools/${schoolId}/`}>
                            <span className='text-lg md:text-xl flex md:items-center gap-x-2.5  cursor-pointer hover:text-gray-100 text-[#787878] font-medium'>
                              <span
                                style={{
                                  background: '#333332CC',
                                }}
                                className='flex w-6 h-6  rounded-full  items-center justify-center'
                              >
                                <BackIconChevron
                                  className=' text-white'
                                  style={{
                                    strokeWidth: '1px',
                                  }}
                                />
                              </span>
                            </span>
                          </Link>
                          <div className='flex gap-x-3'>
                            <LikeComponent
                              likeCount={likeCount}
                              isActive={isActive}
                              toggleHeart={() => {
                                loggedIn
                                  ? toggleHeart()
                                  : setVisible((prevState) => ({
                                      ...prevState,
                                      login: true,
                                      signUp: false,
                                    }));
                              }}
                            />

                            <div className='flex space-x-2 z-20'>
                              <button
                                style={{
                                  background:
                                    'linear-gradient(360deg, #252525 0%, #545454 100%)',
                                }}
                                className=' text-white pl-2 bg-opacity-70 pr-4  h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                                onClick={(e) => shareOnPlatform('linkedin', e)}
                              >
                                <span className='  flex justify-center gap-x-1.5 -translate-y-[1.5px] md:-translate-y-[1px]'>
                                  <LinkedInIcon />{' '}
                                  <p className='pt-[3px]'>share</p>
                                </span>
                              </button>

                              <button
                                className=' text-white p-1 bg-[#222326] bg-opacity-70 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                                onClick={(e) => shareOnPlatform('whatsapp', e)}
                              >
                                <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                                  <WhatsAppIcon />
                                </span>
                              </button>
                              <Dropdown
                                menu={{ items }}
                                arrow={true}
                                placement='bottomLeft'
                              >
                                <button
                                  className=' text-white p-1 bg-[#222326] bg-opacity-70 w-8 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                                  onClick={(e) => shareOnPlatform('', e)}
                                >
                                  <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                                    <ShareIcon
                                      className='w-5 h-5 text-[#AAAAAA]'
                                      style={{ strokeWidth: '1px' }}
                                    />
                                  </span>
                                </button>
                              </Dropdown>
                            </div>
                          </div>
                        </div>
                        <div className='px-2'>
                          <ParserV2
                            className='text-base text-[#A7A7A7] hover:text-white '
                            maxWords={screens.md ? 7 : 6}
                            text={data?.schoolName as string}
                          />
                        </div>
                      </div>
                    </ShouldRender>
                    <ShouldRender check={screens.md}>
                      <div>
                        <span className='text-sm inline-flex font-medium text-[#A7A7A7]'>
                          Reading Time:{' '}
                          {data?.content &&
                            calculateReadingTime(data?.content as string)
                              .minutes}{' '}
                          Mins
                        </span>
                      </div>
                    </ShouldRender>
                  </div>

                  <Typography.Title
                    level={1}
                    className='text-[28px] lg:text-[32px] px-2 pt-4 font-[600] leading-[34px] lg:leading-[38px]'
                  >
                    {data?.title}
                  </Typography.Title>
                  <div className='flex flex-col pt-2 px-2 gap-y-2 md:pt-8'>
                    <ShouldRender check={!screens.md}>
                      <div>
                        <span className='text-sm inline-flex font-medium text-[#A7A7A7]'>
                          Reading Time:{' '}
                          {data?.content &&
                            calculateReadingTime(data?.content as string)
                              .minutes}{' '}
                          Mins
                        </span>
                      </div>
                    </ShouldRender>
                    <div className=' flex justify-between  text-sm '>
                      <div className='flex gap-x-2 items-center'>
                        {/* Author Info */}
                        <div className='flex gap-x-1 flex-col xxxs:flex-row items-center'>
                          <span
                            // onClick={() =>
                            //   router.push(
                            //     `/author/${schoolData?.authorData?.authorUserName}`,
                            //   )
                            // }
                            className='flex gap-x-4 text-white items-center cursor-pointer  w-fit capitalize'
                          >
                            <Avatar
                              size={!screens.sm ? 'small' : 'default'}
                              style={{
                                backgroundColor:
                                  (letterColors as Record<string, string>)[
                                    schoolData.authorData?.name?.charAt(0) || 2
                                  ] || '#000000',
                              }}
                              className={clsx(!avatarSrc && 'bg-primary')}
                              src={avatarSrc}
                            >
                              {!schoolData.authorData?.name ? (
                                <UserOutlined />
                              ) : (
                                schoolData.authorData?.name &&
                                getUserInitials(schoolData.authorData?.name)
                              )}
                            </Avatar>

                            <Typography.Title
                              level={2}
                              className={clsx(
                                !(followCount > 1000) && 'mb-0',
                                ' font-medium text-sm xl:text-base ',
                              )}
                            >
                              {schoolData.authorData?.name}
                            </Typography.Title>
                          </span>
                          <span className='flex flex-col text-fadedWhite'>
                            <ShouldRender check={followCount > 1000}>
                              <span className='text-sm text-textGray translate-x-[24px] xxxs:-translate-x-[10px] whitespace-nowrap'>
                                <Badge
                                  count={
                                    formatCount(followCount as number).formatted
                                  }
                                  className='customBadgeFollow -translate-y-[2.5px] translate-x-[5.5px] text-[#7D7D7D]'
                                  overflowCount={9999}
                                  style={{
                                    backgroundColor: 'transparent',
                                    color: '#7D7D7D',
                                    fontSize: '14px',
                                  }}
                                />{' '}
                                Readers
                              </span>
                            </ShouldRender>
                          </span>
                        </div>
                        <div
                          className={clsx(
                            followCount > 1000 &&
                              '-translate-y-[10px] xxxs:-translate-y-[2px] ',
                            'flex items-start gap-x-2 ',
                          )}
                        >
                          <p>•</p>
                          {/* Follow / Unfollow Text Button */}
                          <motion.div className='cursor-pointer no-select flex items-start '>
                            <motion.span
                              className=' font-medium text-sm'
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.5, ease: 'easeInOut' }}
                            >
                              {isFollowing ? (
                                <motion.span
                                  key='unfollow'
                                  className='text-blue-500'
                                  onClick={() => {
                                    loggedIn
                                      ? setIsModalVisible(true)
                                      : setVisible((prevState) => ({
                                          ...prevState,
                                          login: true,
                                          signUp: false,
                                          noRedirect: true,
                                        }));
                                  }}
                                  initial={{ y: -10, opacity: 0 }}
                                  animate={{ y: 0, opacity: 1 }}
                                  exit={{ y: 10, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  Following
                                </motion.span>
                              ) : (
                                <motion.span
                                  key='follow'
                                  onClick={() => {
                                    loggedIn
                                      ? toggleFollow()
                                      : setVisible((prevState) => ({
                                          ...prevState,
                                          login: true,
                                          signUp: false,
                                          noRedirect: true,
                                        }));
                                  }}
                                  className='text-blue-500'
                                  initial={{ y: 10, opacity: 0 }}
                                  animate={{ y: 0, opacity: 1 }}
                                  exit={{ y: -10, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  Follow
                                </motion.span>
                              )}
                            </motion.span>
                          </motion.div>
                        </div>
                      </div>

                      <ShouldRender check={screens.md}>
                        <div className='flex gap-x-3'>
                          <LikeComponent
                            likeCount={likeCount}
                            isActive={isActive}
                            toggleHeart={() => {
                              loggedIn
                                ? toggleHeart()
                                : setVisible((prevState) => ({
                                    ...prevState,
                                    login: true,
                                    signUp: false,
                                  }));
                            }}
                          />

                          <div className='flex space-x-2'>
                            <button
                              style={{
                                background:
                                  'linear-gradient(360deg, #252525 0%, #545454 100%)',
                              }}
                              className=' text-white pl-2 pr-4  h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                              onClick={(e) => shareOnPlatform('linkedin', e)}
                            >
                              <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                                <LinkedInIcon /> share
                              </span>
                            </button>

                            <button
                              className=' text-white p-1 bg-[#222326]  h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                              onClick={(e) => shareOnPlatform('whatsapp', e)}
                            >
                              <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                                <WhatsAppIcon />
                              </span>
                            </button>
                            <Dropdown
                              trigger={['click']}
                              menu={{ items }}
                              arrow={true}
                              placement='bottom'
                            >
                              <button
                                className=' text-white p-1 bg-[#222326] w-8 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                                onClick={(e) => shareOnPlatform('', e)}
                              >
                                <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                                  <ShareIcon
                                    className='w-5 h-5 text-[#AAAAAA]'
                                    style={{ strokeWidth: '1px' }}
                                  />
                                </span>
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                      </ShouldRender>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ShouldRender check={data?.content}>
          <div className='flex justify-center w-full min-h-screen bg-bgColor'>
            <div className='text-white xl:w-7/12 lg:w-10/12 w-full  xl:px-7 md:px-[70px]  px-[20px]'>
              <Renderer data={dataArray} />
            </div>
          </div>
        </ShouldRender>

        <div
          ref={containerRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className='flex flex-col w-full'
        >
          <div
            className={clsx(
              !data?.next_post ? ' py-3 md:py-5 ' : ' py-3 md:py-5 pb-5 mb-8',
              'flex justify-center',
            )}
          >
            <div className='grid grid-cols-2 items-center w-10/12 gap-x-3 md:gap-x-9'>
              <div>
                <ShouldRender check={data?.previous_post}>
                  <div className='w-full'>
                    <Link
                      href={`/schools/${schoolId}/${data?.previous_post?.slug}/?school_id=${school_id}&post_id=${data?.previous_post?.postId}`}
                    >
                      <Button className='bg-transparent h-full w-full px-0 border group hover:border-[#4b4a4a]  border-[#3B3B3B] text-white text-xs font-medium py-2 rounded-3xl flex gap-x-2 items-center'>
                        <div className='flex gap-x-2 md:gap-x-3 items-center  px-2 md:mr-4 md:py-2 w-full'>
                          <span className='flex items-end pb-0.5'>
                            <ChevronLeftIcon className='w-4 md:w-6 h-6' />
                          </span>
                          <div className='flex flex-col gap-2  w-full text-left'>
                            <span className=' text-[13px] md:text-lg text-[#787878]  group-hover:text-primary'>
                              Previous
                            </span>
                          </div>
                        </div>
                      </Button>
                    </Link>
                  </div>
                </ShouldRender>
              </div>
              <div>
                <ShouldRender check={data?.next_post}>
                  <div className='w-full '>
                    <Link
                      href={`/schools/${schoolId}/${data?.next_post?.slug}/?school_id=${school_id}&post_id=${data?.next_post?.postId}`}
                    >
                      <Button className='bg-transparent h-full w-full px-0 border group hover:border-[#4b4a4a] border-[#3B3B3B] text-white text-xs font-medium py-2 rounded-3xl flex gap-x-2 items-center'>
                        <div className='flex gap-x-2 md:gap-x-3 items-center px-2 md:px-5 md:mr-4 md:py-2 w-full'>
                          <div className='flex flex-col gap-2  w-full text-right'>
                            <span className='group-hover:text-primary text-[#787878] text-[13px] md:text-lg'>
                              Next
                            </span>
                          </div>
                          <span className='flex items-end pb-0.5'>
                            <ChevronRightIcon className='w-4 md:w-6 h-6' />
                          </span>
                        </div>
                      </Button>
                    </Link>
                  </div>
                </ShouldRender>
              </div>
            </div>
          </div>
        </div>
        <ShouldRender check={!data?.next_post && schoolCardData.length}>
          <div className='flex items-center w-full justify-center'>
            <div className='w-11/12  pb-5'>
              <HomepageSlider
                heading='YOU MIGHT LIKE THIS SCHOOLS TOO'
                loading={!!schoolCardData.length}
              >
                <span className='flex '>
                  {schoolCardData?.map((item, index) => (
                    <span key={index} className='p-3'>
                      <SchoolFeaturedCard item={item} />
                    </span>
                  ))}
                </span>
              </HomepageSlider>
            </div>
          </div>
        </ShouldRender>
        <LoginDrawer onCancel={onCloseLogin} visible={visibleDrawer.login} />
        <SignUpDrawer onCancel={onCloseSignUp} visible={visibleDrawer.signUp} />
        <Affix offsetBottom={-9}>
          <div className=' -bottom-0 left-0 w-full z-50 customProcessPageTracking'>
            <Progress
              percent={scrollProgress}
              showInfo={false}
              size='small'
              strokeColor='#FF5612'
              className=' customProcessPageTracking w-full'
            />
          </div>
        </Affix>
      </div>
      <ShouldRender check={showButton && !loggedIn}>
        <div className={clsx('fixed bottom-0 left-0', 'w-full shadow-xl')}>
          <div
            style={{ height: `${height}px` }}
            className='bg-bgColor bg-opacity-80 p-4  backdrop-blur-sm  flex justify-center  '
          >
            <div className=' flex items-center '>
              <div className=''>
                <div className='py-3 flex flex-col gap-y-4 '>
                  <h1 className='text-lg font-semibold flex justify-center gap-x-2    mt-1 leading-10 text-fadedWhite text-center w-full'>
                    Sign up to Continue Reading
                  </h1>
                  <div className='flex justify-center'>
                    <ButtonDefault
                      size={4}
                      rounded={true}
                      variant={ButtonVariants.WHITE}
                      onClick={() => {
                        setVisibleDrawer((prevState) => ({
                          ...prevState,
                          signUp: true,
                        }));
                      }}
                      className='w-2/3 md:w-full text-center text-sm '
                    >
                      <span className='text-bold text-sm text-black py-1 w-full   font-bold whitespace-nowrap'>
                        Sign up to unlock
                      </span>
                    </ButtonDefault>
                  </div>
                </div>

                <span className='text-center font-medium flex text-sm text-white justify-center'>
                  Already have an account?
                  <p
                    onClick={() =>
                      setVisibleDrawer((prevState) => ({
                        ...prevState,
                        login: true,
                        signUp: false,
                      }))
                    }
                    className='text-primary font-bold px-1 hover:font-bold hover:underline cursor-pointer transition-all duration-300 ease-in-out'
                  >
                    Log in
                  </p>
                </span>

                <div className='pb-10 md:pb-20'>
                  <div className='flex gap-x-4 gap-y-2 justify-center pt-10 flex-col md:flex-row'>
                    <span className='flex justify-center '>
                      <ButtonDefault
                        size={4}
                        variant={ButtonVariants.UNSTYLED}
                        className='disabled:bg-bgDarkGray w-2/3 hover:bg-[#181818] disabled:text-gray-300 text-fadedWhite bg-black md:w-full  border border-bgDarkGray shadow-sm text-base'
                        rounded={true}
                        loading={googleLoading}
                        spinColor='gray'
                        disabled={googleLoading}
                        onClick={(e) => {
                          e.preventDefault();
                          handleGoogleLogin();
                        }}
                      >
                        <span className='flex text-white  text-xs items-center font-medium gap-x-3 py-1  whitespace-nowrap'>
                          <span className='w-6 h-6'>
                            <Image
                              src={GoogleIcon}
                              alt='GoogleLogo'
                              className='object-contain'
                            />
                          </span>{' '}
                          Continue with Google
                        </span>
                      </ButtonDefault>
                    </span>
                    <ShouldRender check={showAppleLogin}>
                      <span className='flex justify-center '>
                        <ButtonDefault
                          size={4}
                          variant={ButtonVariants.UNSTYLED}
                          loading={appleLoading}
                          spinColor='gray'
                          rounded={true}
                          disabled={appleLoading}
                          className='disabled:bg-bgDarkGray w-2/3 hover:bg-[#181818] disabled:text-gray-300 text-fadedWhite bg-black md:w-full  border border-bgDarkGray shadow-sm text-base'
                          onClick={(e) => {
                            e.preventDefault();
                            handleAppleLogin();
                          }}
                        >
                          <span className='flex text-white text-xs  items-center font-medium gap-x-3   whitespace-nowrap'>
                            <span className='w-5'>
                              <Image
                                src={AppleLogo}
                                alt='GoogleLogo'
                                className='object-contain'
                              />
                            </span>{' '}
                            Continue with Apple
                          </span>
                        </ButtonDefault>
                      </span>
                    </ShouldRender>
                  </div>
                  <div className='flex justify-center pt-5 md:pt-4'>
                    <div className='text-white text-xs  md:w-2/3 text-center font-normal'>
                      By using our service you are agreeing to our
                      <span className='inline-flex gap-x-1 items-baseline'>
                        <a
                          href='/terms_and_conditions.html'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary font-medium cursor-pointer hover:text-darkPrimary'
                        >
                          Terms
                        </a>{' '}
                        and{' '}
                        <a
                          href='/terms_and_conditions.html'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary font-medium cursor-pointer hover:text-darkPrimary'
                        >
                          conditions
                        </a>
                      </span>
                    </div>
                    <span className=' flex justify-center flex-initial '></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ShouldRender>

      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width='400px'
        className='bg-[#212121] rounded-lg'
        closable={false}
        centered={true}
        destroyOnClose={true}
      >
        <div className='flex flex-col'>
          <div className='py-5'>
            Sure want to Un-Follow {schoolData?.authorData?.name} ?
          </div>
          <div className='flex gap-x-1 justify-end'>
            <span>
              <Button onClick={handleCancel} type='text'>
                No
              </Button>
            </span>
            <span>
              <Button
                onClick={() => {
                  toggleFollow();
                  setIsModalVisible(false);
                }}
                type='text'
                className='text-blue-500 font-medium'
              >
                Yes
              </Button>
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChaptersPage;
